import React, { useState, useEffect } from "react";
import { Table, Empty, Row, Col, Tabs, Tag, Popconfirm, Tooltip, Spin } from "antd";
import { BarsOutlined, StopOutlined } from "@ant-design/icons";
import moment from "moment";
import RequestHandler from "../../util/RequestHandler";
import useSweepAccess from "../../util/useSweepAccess";
import SweepPaymentListFilter from "../Filters/SweepPaymentListFilter";

// EnumSweepPaymentStatus: New = 1, Approved = 2, SentToBank = 3, Completed = 4, Failed = 5, Cancelled = 6
// OData enum'ları string üye adı olarak da serialize edebilir.
const statusMap = {
  1: { text: "Gönderilecek", color: "blue" },
  New: { text: "Gönderilecek", color: "blue" },
  2: { text: "Onaylandı", color: "cyan" },
  Approved: { text: "Onaylandı", color: "cyan" },
  3: { text: "Bankaya Gönderildi", color: "geekblue" },
  SentToBank: { text: "Bankaya Gönderildi", color: "geekblue" },
  4: { text: "Tamamlandı", color: "green" },
  Completed: { text: "Tamamlandı", color: "green" },
  5: { text: "Hatalı", color: "red" },
  Failed: { text: "Hatalı", color: "red" },
  6: { text: "İptal", color: "default" },
  Cancelled: { text: "İptal", color: "default" },
};

const renderStatus = (status) => {
  const item = statusMap[status] || { text: status, color: "default" };
  return <Tag color={item.color}>{item.text}</Tag>;
};

const isNewStatus = (status) => status === 1 || status === "New";

const formatAmount = (value) =>
  Number(value).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const renderAmount = (text, record) => {
  if (isNewStatus(record.status)) {
    if (record.sendAllBalance) return "Tüm Bakiye";
    if (record.minRemainingBalance != null) {
      return `Bakiye - Min ${formatAmount(record.minRemainingBalance)}`;
    }
  }
  if (text || text === 0) return formatAmount(text);
  return "";
};

const SweepPaymentList = () => {
  const { loading: accessLoading, hasAccess } = useSweepAccess();
  const [activeTab, setActiveTab] = useState("new");

  const [data, setData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [take, setTake] = useState(20);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState("");
  const [filterValues, setFilterValues] = useState({});

  const baseFilter =
    activeTab === "new"
      ? "status eq 'New'"
      : activeTab === "failed"
      ? "status eq 'Failed'"
      : activeTab === "cancelled"
      ? "status eq 'Cancelled'"
      : "status ne 'New' and status ne 'Cancelled' and status ne 'Failed'";

  const reList = () => {
    setData((prev) => ({ ...prev, loading: true }));

    const fullFilter = filter ? `${baseFilter} and (${filter})` : baseFilter;

    RequestHandler.get(
      `/SweepPayment?$count=true&$orderby=paymentDate desc&$skip=${skip}&$top=${take}&$filter=${fullFilter}`
    ).then((response) => {
      if (response && Array.isArray(response.value)) {
        setData({
          data: response.value,
          total: response["@odata.count"],
          loading: false,
        });
      } else {
        setData({ data: [], total: 0, loading: false });
      }
    });
  };

  useEffect(() => {
    reList();
  }, [skip, take, filter, activeTab]);

  const onFinishTableFilter = (query, values) => {
    setSkip(0);
    setFilterValues(values);
    setFilter(query);
  };

  const onClearTableFilter = () => {
    setSkip(0);
    setFilterValues({});
    setFilter("");
  };

  const onTabChange = (key) => {
    setActiveTab(key);
    setSkip(0);
    setFilter("");
    setFilterValues({});
  };

  const handleCancel = (id) => {
    RequestHandler.put(`/SweepPayment/Cancel/${id}`).then((response) => {
      if (response && response.success) {
        reList();
      }
    });
  };

  const paging = (p) => {
    setSkip((p.current - 1) * p.pageSize);
    setTake(p.pageSize);
  };

  const baseColumns = [
    { title: "Kural", dataIndex: "ruleName", key: "ruleName" },
    {
      title: "Gönderen Banka",
      dataIndex: "senderBankName",
      key: "senderBankName",
    },
    {
      title: "Gönderen Hesap",
      key: "sender",
      render: (text, record) =>
        `${record.senderAccountName ? record.senderAccountName + " - " : ""}${
          record.senderIban || ""
        }`,
    },
    {
      title: "Alıcı Banka",
      dataIndex: "receiverBankName",
      key: "receiverBankName",
    },
    {
      title: "Alıcı Hesap",
      key: "receiver",
      render: (text, record) => record.receiverIban || "",
    },
    {
      title: "Tutar",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (text, record) => renderAmount(text, record),
    },
    { title: "Para Birimi", dataIndex: "currencyCode", key: "currencyCode" },
    {
      title: "Ödeme Tarihi",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (text) => (text ? moment(text).format("DD.MM.YYYY HH:mm") : ""),
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: (text) => renderStatus(text),
    },
  ];

  const referenceColumn = {
    title: "Referans No",
    dataIndex: "referenceNumber",
    key: "referenceNumber",
    render: (text) => (text ? text : ""),
  };

  const errorColumn = {
    title: "Hata",
    dataIndex: "errorMessage",
    key: "errorMessage",
    ellipsis: true,
  };

  const cancelColumn = {
    title: "",
    key: "cancel",
    align: "center",
    render: (text, record) =>
      isNewStatus(record.status) ? (
        <Popconfirm
          title="Bu ödemeyi iptal etmek istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          onConfirm={() => handleCancel(record.id)}
        >
          <Tooltip title="Ödemeyi İptal Et">
            <a style={{ color: "red", fontSize: "130%" }}>
              <StopOutlined />
            </a>
          </Tooltip>
        </Popconfirm>
      ) : null,
  };

  let columns = baseColumns;
  if (activeTab === "new") {
    columns = [...baseColumns, cancelColumn];
  } else if (activeTab === "failed") {
    columns = [...baseColumns, referenceColumn, errorColumn];
  } else {
    columns = [...baseColumns, referenceColumn];
  }

  if (accessLoading || !hasAccess) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col span={24}>
          <p style={{ fontSize: "120%", marginTop: -9 }}>
            <BarsOutlined /> SÜPÜRME ÖDEMELERİ
          </p>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={onTabChange}>
        <Tabs.TabPane tab="Gönderilecek Ödemeler" key="new" />
        <Tabs.TabPane tab="Gönderilen Ödemeler" key="sent" />
        <Tabs.TabPane tab="Hatalı Ödemeler" key="failed" />
        <Tabs.TabPane tab="İptal Ödemeler" key="cancelled" />
      </Tabs>

      <Row>
        <Col span={24}>
          <SweepPaymentListFilter
            data={filterValues}
            onFinishFilter={onFinishTableFilter}
            onClearFilter={onClearTableFilter}
            style={{ marginTop: 12, marginBottom: 12 }}
          />
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Table
            columns={columns}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Veri Yok"
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%" }}
            size="small"
            rowKey="id"
            onChange={paging}
            pagination={{
              pageSizeOptions: ["15", "25", "50", "100"],
              defaultPageSize: take,
              total: data.total,
            }}
            dataSource={data.data}
            loading={data.loading}
          />
        </Col>
      </Row>
    </div>
  );
};

export default SweepPaymentList;
